import React, { FC, FocusEvent, useCallback, useState } from 'react';

import { Checkbox, TextInput } from '@rbilabs/components-library';
import { IntlFormatters, useIntl } from 'react-intl';

import { CountrySelectInput } from 'components/country-select-input';
import LoadingAnimation from 'components/loading-animation';
import VisuallyHidden from 'components/visually-hidden';
import useCreditCardFormProps from 'hooks/form/use-credit-card-form-props';
import { useLocale } from 'state/intl';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import {
  PaymentFieldVariations,
  defaultPaymentFieldVariation,
} from 'state/launchdarkly/variations';
import { usePaymentContext } from 'state/payment';
import { CardType } from 'state/payment/types';
import { FormValidationState, MapInputNameWithTranslationKey } from 'utils/form';
import { IAdyenPaymentState, IPaymentErrors, IPaymentState, excludeNumeric } from 'utils/payment';

import AdyenCustomCardInputs from './adyen-credit-card-form-inputs/adyen-credit-card-inputs';
import { IAdyenErrors, IAdyenState } from './adyen-credit-card-form-inputs/types';
import { BillingSameAsDeliveryLabel, SaveCardForFutureLabel } from './credit-card-form-labels';
import FormTitle from './credit-card-form-title';
import FirstDataCreditCardInputs from './first-data-credit-card-form-inputs/first-data-credit-card-inputs';
import {
  FormContainer,
  StyledBillingAptWrapper,
  StyledBillingCityWrapper,
  StyledBillingStateWrapper,
  StyledBillingZipWrapper,
  StyledCheckbox,
  StyledCountrySelectWrapper,
  StyledFormContainer,
  StyledNameOnCardWrapper,
  StyledSaveCardContainer,
  StyledStreetAddressWrapper,
} from './styled';

interface GetErrorMessageOptions {
  errors: IPaymentErrors;
  formatMessage: IntlFormatters['formatMessage'];
}

export const getErrorMessage = ({
  errors,
  formatMessage,
}: GetErrorMessageOptions): string | null => {
  const errorKeys = Object.keys(errors);
  const numberOfErrors = errorKeys.filter(error => errors[error]).length;
  if (!numberOfErrors) {
    return null;
  }
  const id = numberOfErrors === 1 ? 'thereIsAnErrorInThisForm' : 'thereAreErrorsInThisForm';
  return numberOfErrors ? formatMessage({ id }, { numberOfErrors }) : null;
};

interface ICreditCardFormInputs {
  errors: IPaymentErrors;
  isDelivery?: boolean;
  onChange: (name: string, value: string) => void;
  onAdyenChange?: (state: IAdyenState) => void;
  onAdyenError?: (errors: IAdyenErrors) => void;
  paymentValues: IPaymentState | IAdyenPaymentState;
  showSaveCard?: boolean;
  withBorder?: boolean;
  ariaLabel?: string;
  supportedCardTypes?: CardType[];
}

const CreditCardFormInputs: FC<ICreditCardFormInputs> = ({
  errors,
  isDelivery = false,
  onChange,
  onAdyenChange,
  onAdyenError: handleAdyenError,
  paymentValues,
  showSaveCard = true,
  withBorder = false,
  ariaLabel,
  supportedCardTypes = [],
}) => {
  const paymentFieldVariations =
    useFlag<PaymentFieldVariations>(LaunchDarklyFlag.PAYMENT_FIELD_VARIATIONS) ||
    defaultPaymentFieldVariation;
  const { feCountryCode } = useLocale();
  // If the country field is hidden from the form, use the user's current locale
  const country = paymentFieldVariations.country ? paymentValues.billingCountry : feCountryCode;

  // retrieve additional credit card form props
  const { postalCodeInputProps, regionInputProps } = useCreditCardFormProps(country ?? undefined);
  const { formatMessage } = useIntl();
  const { isAdyen } = usePaymentContext();
  const [isAdyenInputsLoading, setIsAdyenInputsLoading] = useState(true);
  const onAdyenInputsReady = useCallback(() => {
    setIsAdyenInputsLoading(false);
  }, []);
  const onAdyenError = useCallback(
    (adyenErrors: IAdyenErrors) => {
      setIsAdyenInputsLoading(false);
      if (handleAdyenError) {
        handleAdyenError(adyenErrors);
      }
    },
    [handleAdyenError]
  );

  const isFormLoading = isAdyen ? isAdyenInputsLoading : false;

  const handleChange: React.ChangeEventHandler<HTMLSelectElement | HTMLInputElement> = e =>
    onChange(e.target.name, e.target.value);

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    const fieldName = e.target.name;
    e.target.setAttribute(
      'aria-label',
      formatMessage({ id: MapInputNameWithTranslationKey[fieldName] ?? fieldName })
    );
  };

  const errorMessage = getErrorMessage({ errors, formatMessage });

  return (
    <>
      {isFormLoading && <LoadingAnimation fillColor={Styles.color.primary} />}
      {errorMessage && <VisuallyHidden role="alert">{errorMessage}</VisuallyHidden>}
      <FormContainer withBorder={withBorder} aria-label={ariaLabel} isHidden={isFormLoading}>
        <FormTitle
          title={formatMessage({ id: 'pleaseEnterYourCreditCardInformation' })}
          creditText={formatMessage({ id: 'credit' })}
        />
        {paymentFieldVariations.name && (
          <StyledNameOnCardWrapper>
            <TextInput
              aria-label={formatMessage({ id: 'nameOnCard' })}
              onChange={handleChange}
              onBlur={handleBlur}
              label={formatMessage({ id: 'nameOnCard' })}
              value={paymentValues.nameOnCard}
              autoFocus
              required
              data-testid="nameOnCard"
              errorMessage={errors.nameOnCard}
              name="nameOnCard"
              autoComplete="cc-name"
            />
          </StyledNameOnCardWrapper>
        )}
        {isAdyen ? (
          <AdyenCustomCardInputs
            onChange={onAdyenChange}
            onError={onAdyenError}
            onAdyenInputsReady={onAdyenInputsReady}
          />
        ) : (
          <FirstDataCreditCardInputs
            onChange={handleChange}
            onBlur={handleBlur}
            supportedCardTypes={supportedCardTypes}
            paymentValues={paymentValues}
            errors={errors}
            paymentFieldVariations={paymentFieldVariations}
          />
        )}
        {isDelivery && (
          <Checkbox
            label={<BillingSameAsDeliveryLabel />}
            name="billingAddressSameAsDelivery"
            onChange={handleChange}
            checked={paymentValues.billingAddressSameAsDelivery}
            data-testid="billing-same-as-delivery"
          />
        )}
      </FormContainer>
      <StyledFormContainer withBorder={false} isHidden={isFormLoading}>
        {paymentFieldVariations.addressLine1 && (
          <StyledStreetAddressWrapper>
            <TextInput
              aria-label={formatMessage({ id: 'streetAddress' })}
              onChange={handleChange}
              onBlur={handleBlur}
              label={formatMessage({ id: 'streetAddress' })}
              value={paymentValues.billingStreetAddress || ''}
              required
              data-testid="billingStreetAddress"
              errorMessage={errors.billingStreetAddress}
              name="billingStreetAddress"
              autoComplete="billing street-address"
              readOnly={isDelivery && paymentValues.billingAddressSameAsDelivery}
            />
          </StyledStreetAddressWrapper>
        )}
        {paymentFieldVariations.addressLine2 && (
          <StyledBillingAptWrapper>
            <TextInput
              aria-label={formatMessage({ id: 'apartment' })}
              onChange={handleChange}
              onBlur={handleBlur}
              label={formatMessage({ id: 'aptOrSuite' })}
              value={paymentValues.billingApt || ''}
              data-testid="billingApt"
              errorMessage={errors.billingApt}
              name="billingApt"
              autoComplete="billing street-apt"
              readOnly={isDelivery && paymentValues.billingAddressSameAsDelivery}
            />
          </StyledBillingAptWrapper>
        )}
        {paymentFieldVariations.city && (
          <StyledBillingCityWrapper>
            <TextInput
              aria-label={formatMessage({ id: 'city' })}
              label={formatMessage({ id: 'city' })}
              onChange={handleChange}
              onBlur={handleBlur}
              value={paymentValues.billingCity || ''}
              required
              data-testid="billingCity"
              errorMessage={errors.billingCity}
              name="billingCity"
              autoComplete="billing locality"
              readOnly={isDelivery && paymentValues.billingAddressSameAsDelivery}
            />
          </StyledBillingCityWrapper>
        )}
        {paymentFieldVariations.state && (
          <StyledBillingStateWrapper>
            <TextInput
              aria-label={formatMessage({ id: 'state' })}
              onChange={handleChange}
              onBlur={handleBlur}
              value={excludeNumeric(paymentValues.billingState ?? '')}
              required
              data-testid="billingState"
              errorMessage={errors.billingState}
              name="billingState"
              autoComplete="billing region"
              readOnly={isDelivery && paymentValues.billingAddressSameAsDelivery}
              {...regionInputProps}
            />
          </StyledBillingStateWrapper>
        )}
        {paymentFieldVariations.zip && (
          <StyledBillingZipWrapper>
            {/* Additional props are spread: label, maxLength, pattern */}
            <TextInput
              aria-label={formatMessage({ id: 'zipCode' })}
              onChange={handleChange}
              onBlur={handleBlur}
              value={paymentValues.billingZip}
              required
              data-testid="billingZip"
              errorMessage={errors.billingZip}
              name="billingZip"
              autoComplete="billing postal-code"
              maxLength={10}
              readOnly={isDelivery && paymentValues.billingAddressSameAsDelivery}
              {...postalCodeInputProps}
            />
          </StyledBillingZipWrapper>
        )}
        {paymentFieldVariations.country && (
          <StyledCountrySelectWrapper>
            <CountrySelectInput
              label={formatMessage({ id: 'country' })}
              name="billingCountry"
              data-testid="billingCountry"
              required
              value={paymentValues.billingCountry ?? ''}
              onChange={handleChange}
              autocomplete="billing country"
              validation={errors.billingCountry ? FormValidationState.INVALID : undefined}
              message={errors.billingCountry}
              readOnly={!!(isDelivery && paymentValues.billingCountry)}
            />
          </StyledCountrySelectWrapper>
        )}
      </StyledFormContainer>
      {showSaveCard && (
        <StyledSaveCardContainer>
          <StyledCheckbox
            label={<SaveCardForFutureLabel />}
            name="saveCard"
            data-testid="saveCard"
            checked={paymentValues.saveCard}
            onChange={handleChange}
          />
        </StyledSaveCardContainer>
      )}
    </>
  );
};

export default CreditCardFormInputs;
